/**
 * DemoScreens — CaraLoom Controlled Demo Environment (iter 179).
 *
 * All demo screens live here to keep the surface small and reviewable.
 * Every screen honours the IP protection rules:
 *   - No provider scores, ranking indices, or recommendation percentages
 *   - No mechanism / algorithm / routing / scoring / weighting reveal
 *   - No public workflow disclosure
 *   - Every provider option is labelled "Appropriate Option"
 *   - Every metric is prefixed with "Illustrative demo data"
 */
import React, { useState } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle2, Circle, AlertCircle, ArrowRight, ArrowLeft, ArrowUpRight,
  Hospital, Sparkles, Stethoscope, MapPin, User, Clock, Home, Heart,
  Building2, Filter, TrendingUp, TrendingDown, Eye,
} from 'lucide-react';
import { useDemo } from '../../contexts/DemoContext';
import { ELEANOR_ARCHETYPES } from '../../data/demoData';
import { DemoSectionTitle, StatCard } from './DemoLayout';

/* ═════════════════════ 1. Executive Overview ═════════════════════ */
export function DemoOverview() {
  const { state } = useDemo();
  const kpis = state.kpis;
  const charts = state.charts;

  return (
    <div className="space-y-8" data-testid="demo-overview-page">
      <DemoSectionTitle
        eyebrow="Care transitions · today"
        title="What is happening across our post-acute transitions right now"
        sub="Executive visibility for case-management leadership."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-testid="demo-overview-kpis">
        <StatCard testId="demo-kpi-active"        value={kpis.active}         label="Active Transitions" icon={Circle} />
        <StatCard testId="demo-kpi-confirmed"     value={kpis.confirmed}      label="Placement Confirmed" tone="good" icon={CheckCircle2} />
        <StatCard testId="demo-kpi-in-progress"   value={kpis.in_progress}    label="In Progress" icon={Clock} />
        <StatCard testId="demo-kpi-needs-attention" value={kpis.needs_attention} label="Needs Attention" tone="warn" icon={AlertCircle} />
      </div>

      <div className="grid lg:grid-cols-[1.1fr_.9fr] gap-4 md:gap-6">
        <BarChart
          title="Transition Status"
          rows={charts.status}
          testId="demo-chart-status"
        />
        <BarChart
          title="Placement by Care Setting"
          rows={charts.by_setting}
          testId="demo-chart-by-setting"
        />
      </div>

      <SparkTrend
        title="Time to placement · illustrative trend"
        rows={charts.trend}
        testId="demo-chart-trend"
      />

      <div className="rounded-2xl border border-cn-border bg-cn-bone-100/60 dark:bg-cn-ink-900/40 p-6">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl bg-cn-accent/20 flex items-center justify-center text-cn-accent">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cn-text-muted">CaraLoom intelligence</div>
            <div className="cn-display text-lg text-cn-text mt-1">
              CaraLoom identified appropriate post-acute options for today&apos;s transitions.
            </div>
            <p className="mt-2 text-sm text-cn-text-muted">
              Options reflect the needs of each transition and available post-acute care information.
            </p>
          </div>
        </div>
      </div>

      <ExecutiveLinks />
    </div>
  );
}

function BarChart({ title, rows, testId }) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="rounded-2xl border border-cn-border bg-cn-bg dark:bg-white/[0.02] p-6" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-cn-text-muted">{title}</div>
      <div className="mt-4 space-y-3">
        {rows.map((r) => (
          <div key={r.label} className="grid grid-cols-[80px_1fr_40px] items-center gap-3 text-sm">
            <span className="text-cn-text-muted truncate">{r.label}</span>
            <div className="h-2 rounded-full bg-cn-bone-200 dark:bg-white/5 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(r.value / max) * 100}%`, backgroundColor: r.color }} />
            </div>
            <span className="text-right tabular-nums text-cn-text font-semibold">{r.value}</span>
          </div>
        ))}
      </div>
      <p className="mt-4 text-[10px] text-cn-text-muted italic">Illustrative demo data.</p>
    </div>
  );
}

function SparkTrend({ title, rows, testId }) {
  const max = Math.max(...rows.map((r) => r.value));
  const min = Math.min(...rows.map((r) => r.value));
  const span = Math.max(max - min, 1);
  const points = rows.map((r, i) => {
    const x = (i / (rows.length - 1)) * 100;
    const y = 100 - ((r.value - min) / span) * 100;
    return `${x},${y}`;
  }).join(' ');
  return (
    <div className="rounded-2xl border border-cn-border bg-cn-bg dark:bg-white/[0.02] p-6" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-cn-text-muted">{title}</div>
        <TrendingDown className="w-4 h-4 text-emerald-500" />
      </div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="mt-4 w-full h-32">
        <polyline points={points} fill="none" stroke="#0d9488" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="mt-2 flex justify-between text-[10px] font-mono text-cn-text-muted">
        {rows.map((r) => <span key={r.label}>{r.label}</span>)}
      </div>
      <p className="mt-4 text-[10px] text-cn-text-muted italic">Illustrative demo data.</p>
    </div>
  );
}

function ExecutiveLinks() {
  return (
    <div className="grid md:grid-cols-3 gap-3">
      <Link
        to="/demo/preview/transitions"
        className="rounded-2xl border border-cn-border bg-cn-bg p-5 hover:border-cn-primary/60 transition-colors group"
        data-testid="demo-quick-link-transitions"
      >
        <div className="text-[10px] uppercase tracking-widest text-cn-text-muted">View</div>
        <div className="cn-display text-lg text-cn-text mt-1 flex items-center gap-1">
          Every transition <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </Link>
      <Link
        to="/demo/preview/network"
        className="rounded-2xl border border-cn-border bg-cn-bg p-5 hover:border-cn-primary/60 transition-colors group"
        data-testid="demo-quick-link-network"
      >
        <div className="text-[10px] uppercase tracking-widest text-cn-text-muted">Explore</div>
        <div className="cn-display text-lg text-cn-text mt-1 flex items-center gap-1">
          Post-acute network <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </Link>
      <Link
        to="/demo/preview/insights"
        className="rounded-2xl border border-cn-border bg-cn-bg p-5 hover:border-cn-primary/60 transition-colors group"
        data-testid="demo-quick-link-insights"
      >
        <div className="text-[10px] uppercase tracking-widest text-cn-text-muted">Measure</div>
        <div className="cn-display text-lg text-cn-text mt-1 flex items-center gap-1">
          Transition performance <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </Link>
    </div>
  );
}

/* ═════════════════════ 2. Transitions Queue ═════════════════════ */
export function DemoTransitions() {
  const { state } = useDemo();
  const [filterCare, setFilterCare] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [onlyAttention, setOnlyAttention] = useState(false);
  const rows = state.transitions.filter((r) => {
    if (filterCare !== 'all' && r.need !== filterCare) return false;
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (onlyAttention && r.status !== 'needs_attention') return false;
    return true;
  });
  const cares = ['all', 'Home Health', 'Hospice', 'SNF', 'IRF', 'LTACH'];
  const statuses = ['all', 'confirmed', 'in_progress', 'needs_attention'];

  return (
    <div className="space-y-6" data-testid="demo-transitions-page">
      <DemoSectionTitle
        eyebrow="Case management · queue"
        title="Care transitions"
        sub="Filter by setting, status, or open items requiring intervention."
      />

      <div className="flex flex-wrap gap-2 items-center" data-testid="demo-transitions-filters">
        <span className="text-[10px] uppercase tracking-widest text-cn-text-muted mr-1">
          <Filter className="inline w-3 h-3 mr-1" /> Filters
        </span>
        <select
          value={filterCare}
          onChange={(e) => setFilterCare(e.target.value)}
          className="text-xs bg-cn-bg border border-cn-border rounded-full px-3 py-1.5"
          data-testid="demo-filter-care"
        >
          {cares.map((c) => <option key={c} value={c}>{c === 'all' ? 'All care settings' : c}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="text-xs bg-cn-bg border border-cn-border rounded-full px-3 py-1.5"
          data-testid="demo-filter-status"
        >
          {statuses.map((c) => <option key={c} value={c}>{c === 'all' ? 'All statuses' : humanizeStatus(c)}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setOnlyAttention((v) => !v)}
          className={`text-xs font-medium px-3 py-1.5 rounded-full border ${
            onlyAttention
              ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40'
              : 'bg-cn-bg text-cn-text-muted border-cn-border'
          }`}
          data-testid="demo-filter-attention"
          aria-pressed={onlyAttention}
        >
          Needs attention
        </button>
      </div>

      <div className="rounded-2xl border border-cn-border overflow-hidden bg-cn-bg">
        <table className="w-full text-sm" data-testid="demo-transitions-table">
          <thead className="bg-cn-bone-100 dark:bg-white/[0.03]">
            <tr className="text-left text-[10px] uppercase tracking-widest text-cn-text-muted">
              <th className="px-4 py-3 font-medium">Patient</th>
              <th className="px-4 py-3 font-medium">Post-Acute Need</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Receiving Provider</th>
              <th className="px-4 py-3 font-medium">Expected Care Start</th>
              <th className="px-4 py-3 font-medium text-right">Attention</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((tx) => (
              <tr
                key={tx.id}
                className="border-t border-cn-border/60 hover:bg-cn-bone-100/60 dark:hover:bg-white/[0.02]"
                data-testid={`demo-tx-row-${tx.id}`}
              >
                <td className="px-4 py-3">
                  <Link to={`/demo/preview/patient/${tx.id}`} className="text-cn-text font-semibold hover:text-cn-primary">
                    {tx.patient}
                  </Link>
                  <div className="text-[11px] text-cn-text-muted">Age {tx.age}</div>
                </td>
                <td className="px-4 py-3 text-cn-text-muted">{tx.need}</td>
                <td className="px-4 py-3"><StatusPill status={tx.status} /></td>
                <td className="px-4 py-3 text-cn-text-muted">{tx.provider || '—'}</td>
                <td className="px-4 py-3 text-cn-text-muted">{tx.expected || '—'}</td>
                <td className="px-4 py-3 text-right">
                  {tx.status === 'needs_attention' && (
                    <Link
                      to={`/demo/preview/patient/${tx.id}`}
                      className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 font-semibold"
                    >
                      Review <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-cn-text-muted">
                  No transitions match those filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-cn-text-muted italic">
        Illustrative demo data — fictional patients, fictional providers.
      </p>
    </div>
  );
}

function humanizeStatus(s) {
  return {
    confirmed: 'Placement Confirmed',
    in_progress: 'In Progress',
    needs_attention: 'Needs Attention',
  }[s] || s;
}

function StatusPill({ status }) {
  const map = {
    confirmed: { label: 'Confirmed',      cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
    in_progress: { label: 'In Progress',   cls: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30' },
    needs_attention: { label: 'Needs Attention', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40' },
  };
  const m = map[status] || { label: status, cls: 'bg-cn-bone-100 text-cn-text-muted border-cn-border' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${m.cls}`}>
      {m.label}
    </span>
  );
}

/* ═════════════════════ 3. Patient Detail (Eleanor is primary) ══════ */
export function DemoPatientDetail() {
  const { id } = useParams();
  const { state, archetype } = useDemo();
  const [params] = useSearchParams();
  const view = params.get('view') || 'default';

  // Iter 179 · overlay the presenter-selected archetype onto Eleanor
  // (tx-1) so her need + provider + expected window change in-place
  // without leaving the URL. Other patients are unaffected.
  const baseTx = state.transitions.find((r) => r.id === id) || state.transitions[0];
  const arche  = ELEANOR_ARCHETYPES[archetype] || ELEANOR_ARCHETYPES['home-health'];
  const tx = (baseTx.id === 'tx-1')
    ? { ...baseTx, need: arche.need, provider: arche.provider, expected: arche.expected }
    : baseTx;

  const options = state.providers_by_type[tx.need] || [];

  return (
    <div className="space-y-8" data-testid="demo-patient-page">
      <Link to="/demo/preview/transitions" className="inline-flex items-center gap-1 text-sm text-cn-text-muted hover:text-cn-text" data-testid="demo-back-to-transitions">
        <ArrowLeft className="w-4 h-4" /> Back to transitions
      </Link>

      {/* Patient overview */}
      <div className="grid lg:grid-cols-[1.1fr_.9fr] gap-6">
        <div className="rounded-3xl border border-cn-border bg-cn-bg dark:bg-white/[0.02] p-6 md:p-8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-cn-text-muted mb-3">
            <User className="w-3 h-3" /> Patient · fictional
          </div>
          <h2 className="cn-display text-2xl md:text-3xl text-cn-text leading-tight">{tx.patient}</h2>
          <dl className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <div><dt className="text-[10px] uppercase tracking-widest text-cn-text-muted">Age</dt><dd className="mt-0.5 text-cn-text">{tx.age}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-widest text-cn-text-muted">Hospital</dt><dd className="mt-0.5 text-cn-text">{state.hospital.name}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-widest text-cn-text-muted">Post-acute need</dt><dd className="mt-0.5 text-cn-text">{tx.need}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-widest text-cn-text-muted">Transition status</dt><dd className="mt-0.5"><StatusPill status={tx.status} /></dd></div>
            <div><dt className="text-[10px] uppercase tracking-widest text-cn-text-muted">Receiving provider</dt><dd className="mt-0.5 text-cn-text">{tx.provider || '—'}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-widest text-cn-text-muted">Expected care start</dt><dd className="mt-0.5 text-cn-text">{tx.expected || '—'}</dd></div>
          </dl>

          {tx.status === 'confirmed' && (
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cn-accent/15 text-cn-primary dark:text-cn-accent border border-cn-accent/40">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-widest">On Track</span>
            </div>
          )}
          {tx.status === 'needs_attention' && (
            <div className="mt-6 rounded-xl bg-amber-500/10 border border-amber-500/40 px-4 py-3">
              <div className="text-xs uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">Placement requires attention</div>
              <button type="button" className="mt-2 text-sm text-cn-primary font-semibold" data-testid="demo-review-transition">
                Review Transition →
              </button>
            </div>
          )}

          <p className="mt-6 text-sm text-cn-text-muted leading-relaxed">
            CaraLoom keeps the transition visible from discharge need through the start of
            post-acute care.
          </p>
        </div>

        <PatientJourneyRail tx={tx} />
      </div>

      {/* Placement view */}
      <div id="placement">
        <DemoSectionTitle
          eyebrow="CaraLoom identified appropriate options"
          title="Appropriate Care Options"
          sub={`Options reflect the needs of this transition and available ${tx.need.toLowerCase()} information.`}
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {options.map((p) => {
            const isConfirmed = tx.provider === p.name && tx.status === 'confirmed';
            return (
              <div
                key={p.id}
                className={`rounded-2xl border p-6 ${
                  isConfirmed
                    ? 'border-cn-accent/60 bg-cn-accent/10 dark:bg-cn-accent/10'
                    : 'border-cn-border bg-cn-bg dark:bg-white/[0.02]'
                }`}
                data-testid={`demo-option-${p.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent flex items-center justify-center">
                    {tx.need === 'Home Health' ? <Home className="w-5 h-5" /> :
                     tx.need === 'Hospice' ? <Heart className="w-5 h-5" /> :
                     <Stethoscope className="w-5 h-5" />}
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-cn-primary dark:text-cn-accent font-bold">
                    {p.tag}
                  </span>
                </div>
                <h4 className="mt-4 cn-display text-lg text-cn-text">{p.name}</h4>
                <ul className="mt-3 space-y-1.5 text-xs text-cn-text-muted">
                  <li>Service Area: {p.area}</li>
                  <li>Care Type: {tx.need}</li>
                  {isConfirmed && <li className="text-cn-primary dark:text-cn-accent font-semibold">Status: Confirmed</li>}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Wow-moment confirmation banner */}
      {tx.status === 'confirmed' && (
        <PlacementConfirmedMoment tx={tx} initiallyExpanded={view === 'confirmed'} />
      )}
    </div>
  );
}

function PatientJourneyRail({ tx }) {
  const stops = [
    { key: 'hospital',   icon: Hospital,   title: 'Hospital',          body: 'Ready for post-acute transition.' },
    { key: 'caraloom',   icon: Sparkles,   title: 'CaraLoom',          body: 'Connected transition.', emphasized: true },
    { key: 'post-acute', icon: Stethoscope,title: 'Post-Acute Care',   body: tx.status === 'confirmed' ? 'Placement confirmed.' : 'In progress.' },
    { key: 'care',       icon: CheckCircle2, title: 'Care Start',      body: tx.expected ? `Expected ${tx.expected.toLowerCase()}.` : 'To be confirmed.' },
  ];
  return (
    <div className="rounded-3xl border border-cn-border bg-cn-bone-100/60 dark:bg-cn-ink-900/40 p-6 md:p-8">
      <div className="text-[10px] uppercase tracking-widest text-cn-text-muted mb-4">Transition · patient journey</div>
      <ol className="space-y-3">
        {stops.map((s, i) => (
          <li key={s.key} className={`flex items-start gap-3 ${s.emphasized ? 'font-semibold' : ''}`}>
            <div className={`flex-none w-9 h-9 rounded-xl flex items-center justify-center ${
              s.emphasized
                ? 'bg-cn-primary/15 dark:bg-cn-accent/20 text-cn-primary dark:text-cn-accent'
                : 'bg-cn-bone-200 dark:bg-white/5 text-cn-text-muted'
            }`}>
              <s.icon className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-cn-text">{s.title}</div>
              <div className="text-xs text-cn-text-muted">{s.body}</div>
            </div>
            {i < stops.length - 1 && <div className="text-cn-text-muted opacity-40 text-xs mt-1">↓</div>}
          </li>
        ))}
      </ol>
      <p className="mt-4 text-[10px] text-cn-text-muted italic">
        This represents the patient&apos;s journey — not CaraLoom&apos;s internal processing.
      </p>
    </div>
  );
}

function PlacementConfirmedMoment({ tx }) {
  return (
    <section
      className="rounded-3xl bg-gradient-to-br from-cn-primary via-cn-primary to-cn-secondary text-white p-8 md:p-12 shadow-2xl"
      data-testid="demo-placement-confirmed-moment"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/70 font-mono">
        <CheckCircle2 className="w-4 h-4 text-cn-accent" /> Placement Confirmed
      </div>
      <h2 className="mt-4 cn-display text-3xl md:text-5xl font-bold leading-[1.05] !text-white max-w-3xl">
        {tx.patient} <span className="italic font-light">has a confirmed post-acute placement with</span> {tx.provider}.
      </h2>
      <p className="mt-6 text-lg text-white/85">
        Expected care start: <span className="text-cn-accent font-semibold">{tx.expected}</span>
      </p>
      <div className="mt-8 text-white/70 italic border-l-2 border-cn-accent pl-4">
        The referral isn&apos;t where visibility ends.
      </div>
    </section>
  );
}

/* ═════════════════════ 4. Network View ═════════════════════ */
export function DemoNetwork() {
  const { state } = useDemo();
  const [filterCare, setFilterCare] = useState('all');
  const groups = Object.entries(state.providers_by_type);
  const filtered = filterCare === 'all' ? groups : groups.filter(([type]) => type === filterCare);

  return (
    <div className="space-y-6" data-testid="demo-network-page">
      <DemoSectionTitle
        eyebrow="One connected view"
        title="Post-acute network"
        sub="Every provider, grouped by level of care. Filter as you would in an executive review."
      />
      <div className="flex flex-wrap gap-2" data-testid="demo-network-filters">
        {['all', ...Object.keys(state.providers_by_type)].map((c) => (
          <button
            key={c}
            onClick={() => setFilterCare(c)}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              filterCare === c
                ? 'bg-cn-primary text-white border-cn-primary'
                : 'bg-cn-bg text-cn-text-muted border-cn-border hover:text-cn-text'
            }`}
            data-testid={`demo-network-filter-${c.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {c === 'all' ? 'All care types' : c}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {filtered.map(([type, list]) => (
          <div key={type} className="rounded-2xl border border-cn-border bg-cn-bg dark:bg-white/[0.02] p-6" data-testid={`demo-network-group-${type.replace(/\s+/g,'-').toLowerCase()}`}>
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-4 h-4 text-cn-primary dark:text-cn-accent" />
              <h3 className="cn-display text-lg text-cn-text">{type}</h3>
              <span className="text-xs text-cn-text-muted">· {list.length} providers</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map((p) => (
                <div key={p.id} className="rounded-xl border border-cn-border bg-cn-bone-100/60 dark:bg-cn-ink-900/40 p-4">
                  <div className="text-sm font-semibold text-cn-text">{p.name}</div>
                  <div className="text-[11px] text-cn-text-muted mt-0.5">Service Area: {p.area}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-cn-text-muted italic">
        Illustrative demo data. Provider inclusion, ordering, and availability do not reflect real-world performance.
      </p>
    </div>
  );
}

/* ═════════════════════ 5. Insights (executive analytics) ══════════ */
export function DemoInsights() {
  const { state } = useDemo();
  const ins = state.insights;
  return (
    <div className="space-y-8" data-testid="demo-insights-page">
      <DemoSectionTitle
        eyebrow="Executive analytics"
        title="Transition Performance"
        sub={ins.disclaimer}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard testId="demo-insight-median"    value={ins.median_time_to_placement} label="Median Time to Placement" icon={Clock} />
        <StatCard testId="demo-insight-confirmed" value={ins.confirmed_placement_pct}  label="Confirmed Placement"      tone="good" icon={CheckCircle2} />
        <StatCard testId="demo-insight-visibility"value={ins.transition_visibility_pct}label="Transition Visibility"    tone="good" icon={Eye} />
        <StatCard testId="demo-insight-attention" value={ins.needs_attention_pct}      label="Needs Attention"          tone="warn" icon={AlertCircle} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <BarChart title="Transition Status" rows={state.charts.status} testId="demo-insights-chart-status" />
        <BarChart title="Placement by Care Setting" rows={state.charts.by_setting} testId="demo-insights-chart-setting" />
      </div>
      <SparkTrend title="Time to placement · illustrative trend" rows={state.charts.trend} testId="demo-insights-chart-trend" />

      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-5">
        <div className="text-[11px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">
          {ins.disclaimer}
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════ 6. Pilot Discussion (final screen) ════════ */
export function DemoPilotDiscussion() {
  const steps = [
    { title: 'Start Focused',    body: 'One hospital, unit or defined transition population.' },
    { title: 'Establish Baseline', body: 'Understand current transition performance.' },
    { title: 'Launch CaraLoom',  body: 'Introduce the platform within a defined scope.' },
    { title: 'Measure',          body: 'Evaluate agreed transition outcomes.' },
  ];
  const measures = ['Time to Placement', 'Transition Visibility', 'Manual Touches', 'Placement Outcomes', 'Care-Start Visibility'];
  const navigate = useNavigate();
  return (
    <div className="space-y-10" data-testid="demo-pilot-page">
      <DemoSectionTitle
        eyebrow="Pilot discussion"
        title="What would CaraLoom look like in your organization?"
      />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        {steps.map((s) => (
          <div key={s.title} className="rounded-2xl border border-cn-border bg-cn-bg p-5" data-testid={`demo-pilot-step-${s.title.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="cn-display text-lg text-cn-text">{s.title}</div>
            <p className="mt-2 text-sm text-cn-text-muted leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>

      <div className="rounded-3xl border border-cn-border bg-cn-bone-100/70 dark:bg-cn-ink-900/50 p-8">
        <div className="text-[10px] uppercase tracking-widest text-cn-text-muted mb-3">Potential measures</div>
        <div className="flex flex-wrap gap-2">
          {measures.map((m) => (
            <span key={m} className="text-xs px-3 py-1.5 rounded-full bg-cn-bg border border-cn-border text-cn-text">
              {m}
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-3xl bg-cn-primary text-white p-8 md:p-12 text-center shadow-2xl">
        <div className="cn-display text-3xl md:text-4xl !text-white leading-tight max-w-2xl mx-auto">
          Don&apos;t just send the referral.{' '}
          <span className="italic text-cn-accent">Close the transition.</span>
        </div>
        <p className="mt-4 text-white/85 max-w-xl mx-auto text-sm md:text-base">
          CaraLoom connects hospitals and post-acute care through a more visible, coordinated transition.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href="/?inquiry=pilot"
            className="cn-btn cn-btn-accent text-base font-bold"
            data-testid="demo-pilot-cta-primary"
          >
            Explore a CaraLoom Pilot <ArrowUpRight className="w-4 h-4" />
          </a>
          <button
            type="button"
            onClick={() => navigate('/demo/preview')}
            className="cn-btn bg-white text-cn-primary hover:bg-white/95 text-sm font-bold"
            data-testid="demo-pilot-cta-back"
          >
            Return to Overview <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
